import React, { useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import {
  MDBContainer,
  MDBCol,
  MDBRow,
  MDBBtn,
  MDBInput,
  MDBCheckbox
} from 'mdb-react-ui-kit';
import CompanyNameModal from '../Modals/CompanyNameModal';

const API_BASE_URL = "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/auth";

function Login({ onSwitchToRegister, onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Company Name Modal States
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [pendingGoogleUser, setPendingGoogleUser] = useState(null);

  // =====================
  // EMAIL / PASSWORD LOGIN
  // =====================
  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      alert('⚠️ Email aur password dono required hain');
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || 'Login failed');
        return;
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      alert(`✅ Welcome ${data.user.name}!`);
      onLoginSuccess && onLoginSuccess();

    } catch (err) {
      console.error('LOGIN ERROR:', err);
      alert('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // =====================
  // GOOGLE LOGIN - STEP 1: Get User Info
  // =====================
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setGoogleLoading(true);

        // Get user info from Google
        const userInfoResponse = await fetch(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.access_token}`,
            },
          }
        );

        const userInfo = await userInfoResponse.json();
        console.log('👤 Google user info:', userInfo);

        // Check if user exists in backend
        const checkResponse = await fetch(`${API_BASE_URL}/check-google-user`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: userInfo.email,
          }),
        });

        const checkData = await checkResponse.json();

        if (checkData.exists && checkData.has_company) {
          // User exists and has company - Direct login
          await completeGoogleAuth(tokenResponse.access_token, userInfo, null);
        } else {
          // New user OR user without company - Show modal
          setPendingGoogleUser({
            token: tokenResponse.access_token,
            userInfo: userInfo
          });
          setShowCompanyModal(true);
        }

      } catch (err) {
        console.error('❌ Google login error:', err);
        alert('Google login failed. Please try again.');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error('❌ Google login failed:', error);
      alert('Google login failed. Please try again.');
    },
  });

  // =====================
  // GOOGLE LOGIN - STEP 2: Complete Auth with Company Name
  // =====================
  const completeGoogleAuth = async (token, userInfo, companyName) => {
    try {
      const backendResponse = await fetch(`${API_BASE_URL}/google-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          email: userInfo.email,
          name: userInfo.name,
          company_name: companyName || `${userInfo.name}'s Company`,
        }),
      });

      const backendData = await backendResponse.json();

      if (!backendResponse.ok) {
        alert(backendData.message || 'Google authentication failed');
        return;
      }

      localStorage.setItem('token', backendData.token);
      localStorage.setItem('user', JSON.stringify(backendData.user));

      alert(`✅ Welcome ${backendData.user.name}!`);
      onLoginSuccess && onLoginSuccess();

    } catch (err) {
      console.error('❌ Google auth completion error:', err);
      alert('Authentication failed. Please try again.');
    }
  };

  // =====================
  // HANDLE COMPANY NAME SUBMISSION
  // =====================
  const handleCompanyNameSubmit = async (companyName) => {
    if (!pendingGoogleUser) return;

    await completeGoogleAuth(
      pendingGoogleUser.token,
      pendingGoogleUser.userInfo,
      companyName
    );

    setShowCompanyModal(false);
    setPendingGoogleUser(null);
  };

  return (
    <>
      <MDBContainer fluid className="p-3" style={{ minHeight: '100vh' }}>
        <MDBRow className="justify-content-center align-items-center" style={{ minHeight: '100vh' }}>

          {/* ============================================ */}
          {/* LEFT: IMAGE SECTION */}
          {/* ============================================ */}
          <MDBCol 
            xs="12" 
            md="6" 
            lg="6"
            className="d-none d-md-flex align-items-center justify-content-center"
            style={{ padding: '2rem' }}
          >
            <div style={{ maxWidth: '100%', width: '100%' }}>
              <img
                src="/images/man_in_brown_coat_shopify_ebay_ad.png"
                className="img-fluid"
                alt="Login"
                style={{ 
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  maxHeight: '80vh'
                }}
              />
            </div>
          </MDBCol>

          {/* ============================================ */}
          {/* RIGHT: LOGIN FORM */}
          {/* ============================================ */}
          <MDBCol 
            xs="12" 
            md="6" 
            lg="5" 
            xl="4"
            className="d-flex justify-content-center"
            style={{ padding: '2rem' }}
          >
            <div style={{ width: '100%', maxWidth: '450px' }}>

              {/* LOGO & TITLE */}
              <div className="text-center mb-4">
                <img
                  src="/images/Thirift Lyst PNG.png"
                  alt="ThriftLogo"
                  style={{ 
                    height: 'clamp(60px, 10vw, 80px)',
                    marginBottom: '1rem',
                    width: 'auto'
                  }}
                />
                <p className="text-muted" style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>
                  List once, sell everywhere
                </p>
              </div>

              {/* GOOGLE LOGIN BUTTON */}
              <MDBBtn
                className="mb-3 w-100 d-flex align-items-center justify-content-center"
                size="lg"
                style={{
                  backgroundColor: '#fff',
                  color: '#000',
                  border: '1px solid #ddd',
                  padding: 'clamp(0.6rem, 2vw, 0.8rem)',
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                }}
                onClick={() => googleLogin()}
                disabled={googleLoading}
              >
                {googleLoading ? (
                  "Signing in..."
                ) : (
                  <>
                    <img
                      src="/images/google-icon.svg"
                      alt="Google"
                      style={{ 
                        width: 'clamp(18px, 3vw, 22px)', 
                        height: 'clamp(18px, 3vw, 22px)', 
                        marginRight: '10px' 
                      }}
                    />
                    Sign in with Google
                  </>
                )}
              </MDBBtn>

              {/* DIVIDER */}
              <div className="divider d-flex align-items-center my-3">
                <p className="fw-bold mx-3 mb-0" style={{ color: '#888', fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}>
                  OR
                </p>
              </div>

              {/* LOGIN FORM */}
              <form onSubmit={handleLogin}>
                
                <MDBInput
                  label="Email address"
                  id="email"
                  type="email"
                  size="lg"
                  className="mb-3"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}
                />

                <MDBInput
                  label="Password"
                  id="password"
                  type="password"
                  size="lg"
                  className="mb-3"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}
                />

                <div className="d-flex justify-content-between align-items-center mb-3" 
                     style={{ fontSize: 'clamp(0.85rem, 2vw, 0.95rem)' }}>
                  <MDBCheckbox 
                    label="Remember me" 
                    id="rememberMe"
                  />
                  <a href="#!" style={{ textDecoration: 'none' }}>
                    Forgot password?
                  </a>
                </div>

                <MDBBtn
                  type="submit"
                  className="w-100"
                  size="lg"
                  disabled={loading}
                  style={{ 
                    backgroundColor: '#667eea',
                    padding: 'clamp(0.6rem, 2vw, 0.8rem)',
                    fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                  }}
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </MDBBtn>

              </form>

              {/* REGISTER LINK */}
              <div className="text-center mt-3" style={{ fontSize: 'clamp(0.85rem, 2vw, 0.95rem)' }}>
                <p className="mb-0">
                  Not a member?{' '}
                  <a 
                    href="#!" 
                    onClick={onSwitchToRegister} 
                    style={{ cursor: 'pointer', textDecoration: 'none', fontWeight: '500' }}
                  >
                    Register here
                  </a>
                </p>
              </div>

            </div>
          </MDBCol>

        </MDBRow>
      </MDBContainer>

      {/* ============================================ */}
      {/* COMPANY NAME MODAL */}
      {/* ============================================ */}
      <CompanyNameModal
        show={showCompanyModal}
        userName={pendingGoogleUser?.userInfo?.name || ''}
        onSubmit={handleCompanyNameSubmit}
      />
    </>
  );
}

export default Login;